import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "formgrid grid" }
const _hoisted_2 = { class: "error-msg" }
const _hoisted_3 = { class: "error-msg" }
const _hoisted_4 = { class: "error-msg" }
const _hoisted_5 = { class: "formgrid grid" }
const _hoisted_6 = { class: "field col flex flex-column" }

import { computed, onMounted, reactive, watch, ref } from 'vue';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import { PersonStores, useDisciplineStore } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { TreatmentPlanBaseInterface, TreatmentPlanCategoryInterface } from '@/types';

interface GeneralInterface {
  id?: string;
  start_at?: string;
  person_id?: string;
  treatment_plan_type?: TreatmentPlanBaseInterface;
  treatment_plan_category?: TreatmentPlanCategoryInterface;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'General',
  setup(__props) {

const toast = useToast();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const profileStore = PersonStores.useUserProfileStore();
const disciplineStore = useDisciplineStore();
const referredDiscipline = ref({});

let general: GeneralInterface = reactive({
  id: undefined,
  start_at: undefined,
  treatment_plan_type: undefined,
  treatment_plan_category: undefined,
  person_id: undefined,
});

const rules = {
  start_at: { required },
  treatment_plan_type: { required },
  treatment_plan_category: { required },
};

const setTreatmentInfo = async () => {
  disciplineStore.getDisciplines();
  if (treatmentPlansStore.userTreatmentPlans) {
    const { id, start_at, treatment_plan_type, treatment_plan_category, person_id } =
      treatmentPlansStore.userTreatmentPlans;
    general.id = id;
    general.start_at = start_at;
    general.treatment_plan_type = treatment_plan_type;
    general.treatment_plan_category = treatment_plan_category;
    general.person_id = person_id;
  }
};

onMounted(() => setTreatmentInfo());

watch(() => treatmentPlansStore.userTreatmentPlans, setTreatmentInfo);

const v$ = useVuelidate(rules, general);

const calendarStartDate = computed({
  get: () => {
    if (general.start_at) {
      let segments: (string | number)[] = general.start_at.split('-');
      segments[1] = (segments[1] as number) - 1;
      return new Date(...segments);
    } else {
      return null;
    }
  },
  // eslint-disable-next-line
  set: (value: any) => {
    const d = new Date(value).toISOString();
    const newDate = d.substring(0, 10);
    if (newDate != general.start_at) {
      v$.value.start_at.$touch();
    }
    general.start_at = newDate;
  },
});

const isLoading = computed(() => treatmentPlansStore.loading);

const cloneTreatmentPlan = () => {
  return { ...treatmentPlansStore.userTreatmentPlans };
};

const onCancel = () => {
  if (treatmentPlansStore.userTreatmentPlans?.person_id) {
    const { start_at, treatment_plan_type, treatment_plan_category } = cloneTreatmentPlan();
    general.start_at = start_at as string;
    general.treatment_plan_type = treatment_plan_type;
    general.treatment_plan_category = treatment_plan_category;
    treatmentPlansStore.getTreatmentPlanCategories();
  } else {
    general.treatment_plan_category = undefined;
    general.treatment_plan_type = undefined;
    general.start_at = '';
  }
  v$.value.$reset();
};

const onPlanTypeChange = () => {
  treatmentPlansStore.getTreatmentPlanCategories(general.treatment_plan_type.id);
  general.treatment_plan_category = undefined;
};

const doSuccess = (message: string) => {
  toast.add({
    severity: 'success',
    summary: 'Saved',
    detail: message,
    life: 3000,
  });
};

const doError = (message: string) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: message,
    life: 3000,
  });
};

const updateGeneralTreatmentPlan = async () => {
  await treatmentPlansStore
    .updateGeneralTreatmentPlan()
    .then(() => {
      doSuccess('Treatment Plan has been saved');
      v$.value.$reset();
    })
    .catch((response) => {
      treatmentPlansStore.userTreatmentPlans = treatmentPlansStore.clonedUserTreatmentPlans;
      onCancel();
      treatmentPlansStore.error = undefined;
      doError(response.statusText);
    });
};

const addNewTreatmentPlan = async () => {
  await treatmentPlansStore
    .addNewTreatmentPlan()
    .then(() => {
      doSuccess('Treatment Plan has been created');
      v$.value.$reset();
    })
    .catch((response) => {
      treatmentPlansStore.userTreatmentPlans = treatmentPlansStore.clonedUserTreatmentPlans;
      onCancel();
      treatmentPlansStore.error = undefined;
      doError(response.statusText);
    });
};

const onSave = async () => {
  v$.value.$touch();

  if (!v$.value.$invalid) {
    const generalBackup = cloneTreatmentPlan();

    treatmentPlansStore.userTreatmentPlans = generalBackup;
    treatmentPlansStore.userTreatmentPlans.start_at = general.start_at as string;
    treatmentPlansStore.userTreatmentPlans.treatment_plan_type =
      general.treatment_plan_type as TreatmentPlanBaseInterface;
    treatmentPlansStore.userTreatmentPlans.treatment_plan_category =
      general.treatment_plan_category as TreatmentPlanCategoryInterface;

    if (treatmentPlansStore.userTreatmentPlans?.person_id) {
      await updateGeneralTreatmentPlan();
    } else {
      await addNewTreatmentPlan();
    }
  }
  return;
};

watch(
  () => profileStore.person,
  (newVal) => {
    referredDiscipline.value = newVal?.referred_discipline?.[0]?.discipline_id;
  },
  { immediate: true },
);

const referrerDisciplineChange = () => {
  profileStore.updateReferredDiscipline(referredDiscipline.value ? [referredDiscipline.value] : []);
};

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "General",
    class: "card-full-width",
    dirty: _unref(v$).$anyDirty,
    cancel: onCancel,
    save: onSave
  }, {
    form: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: _normalizeClass(["field col flex flex-column", { error: _unref(v$).treatment_plan_type.$errors.length }])
        }, [
          _cache[6] || (_cache[6] = _createElementVNode("label", { for: "treatmentTypes" }, "Treatment Plan Type", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : _createCommentVNode("", true),
          (!isLoading.value)
            ? (_openBlock(), _createBlock(_component_Dropdown, {
                key: 1,
                id: "treatmentTypes",
                modelValue: _unref(general).treatment_plan_type,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(general).treatment_plan_type) = $event)),
                options: _unref(treatmentPlansStore).treatmentPlanTypes,
                "option-label": "name",
                placeholder: "Select a Treatment Plan Type",
                class: _normalizeClass({ 'p-invalid': _unref(v$).treatment_plan_type.$invalid && _unref(v$).treatment_plan_type.$dirty }),
                disabled: !_unref(can)([_unref(PERMISSIONS).PERSON_TREATMENT_PLANS_UPDATE_STUDENTS]),
                onChange: _cache[1] || (_cache[1] = ($event: any) => {
              _unref(v$).treatment_plan_type.$touch();
              onPlanTypeChange();
            })
              }, null, 8, ["modelValue", "options", "class", "disabled"]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).treatment_plan_type.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_2, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["field col flex flex-column", { error: _unref(v$).treatment_plan_category.$errors.length }])
        }, [
          _cache[7] || (_cache[7] = _createElementVNode("label", { for: "treatmentCategory" }, "Treatment Category", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : _createCommentVNode("", true),
          (!isLoading.value)
            ? (_openBlock(), _createBlock(_component_Dropdown, {
                key: 1,
                id: "treatmentCategory",
                modelValue: _unref(general).treatment_plan_category,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(general).treatment_plan_category) = $event)),
                options: _unref(treatmentPlansStore).treatmentPlanCategories,
                "option-label": "name",
                placeholder: "Select a Category",
                class: _normalizeClass({ 'p-invalid': _unref(v$).treatment_plan_category.$invalid && _unref(v$).treatment_plan_category.$dirty }),
                disabled: !_unref(can)([_unref(PERMISSIONS).PERSON_TREATMENT_PLANS_UPDATE_STUDENTS]),
                onChange: _unref(v$).treatment_plan_category.$touch
              }, null, 8, ["modelValue", "options", "class", "disabled", "onChange"]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).treatment_plan_category.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_3, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["field col flex flex-column", { error: _unref(v$).start_at.$errors.length }])
        }, [
          _cache[8] || (_cache[8] = _createElementVNode("label", { for: "treatmentStartDate" }, "Treatment Start Date", -1)),
          (isLoading.value)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : _createCommentVNode("", true),
          (!isLoading.value)
            ? (_openBlock(), _createBlock(_unref(Calendar), {
                key: 1,
                modelValue: calendarStartDate.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((calendarStartDate).value = $event)),
                placeholder: "Select a Treatment Start Date",
                "date-format": "mm/dd/yy",
                disabled: !_unref(can)([_unref(PERMISSIONS).PERSON_TREATMENT_PLANS_UPDATE_STUDENTS]),
                class: _normalizeClass({ 'p-invalid': _unref(v$).start_at.$invalid && _unref(v$).start_at.$dirty })
              }, null, 8, ["modelValue", "disabled", "class"]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).start_at.$errors, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors"
            }, [
              _createElementVNode("div", _hoisted_4, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ], 2)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _cache[9] || (_cache[9] = _createElementVNode("label", { for: "treatmentTypes" }, "Referred Discipline", -1)),
          (_unref(disciplineStore).loading)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : _createCommentVNode("", true),
          (!_unref(disciplineStore).loading)
            ? (_openBlock(), _createBlock(_component_Dropdown, {
                key: 1,
                modelValue: referredDiscipline.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((referredDiscipline).value = $event)),
                options: _unref(disciplineStore).disciplines,
                "option-label": "name",
                "option-value": "id",
                placeholder: "Select a discipline",
                onChange: _cache[5] || (_cache[5] = ($event: any) => (referrerDisciplineChange()))
              }, null, 8, ["modelValue", "options"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["dirty"]))
}
}

})